/**
 * ENG-6174 — pure decision helper for VERIFYING that a hot-reload MCP restart
 * actually completed.
 *
 * Background: when an integration is connected mid-session, the manager writes a
 * fresh `.mcp.json`, injects an `mcp-update` notice, and calls
 * `scheduleSessionRestart()` so the Claude session respawns and binds the new
 * MCP server. Pre-ENG-6174 this was fire-and-forget: the manager stopped the
 * session and assumed the respawn succeeded. When a respawn did NOT complete
 * cleanly (the canonical case: a restart that stalled after the kill-switch
 * test), the agent sat in its pre-restart state indefinitely and nothing
 * surfaced it — the console said "Connected" while the live session never bound
 * the tools.
 *
 * This helper is the decision the manager consults each poll for an agent with a
 * restart in flight. It is deliberately OBSERVATIONAL: it decides whether the
 * respawn is verified, still pending, or overdue — it does NOT itself force a
 * re-respawn. Forcing one would fight the very gates (circuit breaker,
 * maintenance window, kill-switch) that can legitimately hold a session down;
 * the correct behaviour for a stalled restart is to SURFACE it loudly, not to
 * fight whatever is holding it.
 *
 * Extracted from manager-worker.ts (like mcp-config-drift.ts) so the decision
 * tree is unit-testable without importing the worker's heavy module-load side
 * effects.
 *
 * "Verified" means three things hold together:
 *   1. the tracked session has a start time strictly AFTER the restart fired
 *      (so it's the respawned process, not the killed one),
 *   2. it is currently healthy, AND
 *   3. it carries a real spawn identity (a non-null session id).
 *
 * The third guard is load-bearing (CodeRabbit, ENG-6174). When `tmux
 * kill-session` fails, stopPersistentSession still drops the Map entry, so the
 * next poll's isSessionHealthy() re-discovers the LINGERING (un-killed) tmux
 * session and rehydrates it with `startedAt: Date.now()` and
 * `currentSessionId: null`. Without guard 3, that zombie satisfies "started
 * after firedAt + healthy" and a stalled restart is falsely marked verified —
 * suppressing the very escalation this helper exists to raise. A genuine
 * manager respawn (spawnSession) always assigns a fresh session id, so requiring
 * a non-null id excludes the rehydrated zombie. (A real respawn briefly observed
 * before its id is assigned simply stays "waiting" one more poll — never a false
 * verify, never a false escalate within the grace window.)
 *
 * Anything not verified is either still within the grace window ("waiting") or,
 * once the window lapses, "unverified" — escalating WARN→ERROR across a bounded
 * number of attempts so a genuinely-stuck agent produces one greppable ERROR
 * line rather than silence or a per-poll flood.
 */

import { isWithinReconnectQuiesceWindow } from './mcp-config-lookup.js';

/** Default grace window before an un-respawned restart is flagged (per attempt). */
export const RESTART_VERIFY_DEADLINE_MS = 90_000;

/** Default number of grace windows before escalating WARN → ERROR. */
export const RESTART_VERIFY_MAX_ATTEMPTS = 3;

export interface PendingRestartVerification {
  /** Epoch ms the restart's stop actually fired (post-gate). */
  firedAt: number;
  /**
   * How many grace windows have already lapsed without a verified respawn.
   * Starts at 0 when the restart is first recorded; bumped on each lapse.
   */
  attempts: number;
  /**
   * ENG-7575: how many times the terminal bind-remediation has been DEFERRED
   * because a freshly-added integration was still connectivity `transient_error`
   * (first-connect not settled). Bounds the total wait so a persistently-flapping
   * integration eventually falls through to the normal remediation / ERROR path.
   * Undefined = 0 (never deferred).
   */
  connectivityDeferrals?: number;
}

export type RestartVerifyOutcome =
  /** Respawn confirmed: a healthy session started after the restart fired. */
  | { kind: 'verified' }
  /** Still inside the grace window — keep watching, no log. */
  | { kind: 'waiting' }
  /**
   * Grace window lapsed without a verified respawn. `attempt` is the 1-based
   * lapse count; `final` is true once it reaches maxAttempts (escalate to ERROR
   * and stop watching), false for an intermediate WARN that re-arms the window.
   */
  | { kind: 'unverified'; attempt: number; final: boolean };

/**
 * Decide the verification outcome for one pending restart.
 *
 * @param pending          the recorded in-flight restart
 * @param sessionStartedAt the running session's start time, or null if no
 *                         session is currently tracked (killed / never respawned)
 * @param sessionHealthy   whether the running session is currently healthy
 * @param sessionRespawnId the running session's spawn identity (currentSessionId),
 *                         or null. A genuine manager respawn assigns a fresh id;
 *                         a session rehydrated-on-discovery (e.g. a zombie left by
 *                         a failed kill) has null — which must NOT count as a
 *                         respawn (see guard 3 above).
 * @param now              current epoch ms
 */
export function decidePostRestartVerification(
  pending: PendingRestartVerification,
  sessionStartedAt: number | null,
  sessionHealthy: boolean,
  sessionRespawnId: string | null,
  now: number,
  opts: { deadlineMs?: number; maxAttempts?: number } = {},
): RestartVerifyOutcome {
  const deadlineMs = opts.deadlineMs ?? RESTART_VERIFY_DEADLINE_MS;
  const maxAttempts = opts.maxAttempts ?? RESTART_VERIFY_MAX_ATTEMPTS;

  // A genuine respawn: a HEALTHY session that started strictly after the restart
  // fired AND carries a real spawn identity. The identity guard rejects a zombie
  // session rehydrated by isSessionHealthy() after a failed kill (startedAt=now,
  // currentSessionId=null) — see the file header.
  if (
    sessionStartedAt !== null &&
    sessionStartedAt > pending.firedAt &&
    sessionHealthy &&
    sessionRespawnId !== null
  ) {
    return { kind: 'verified' };
  }

  // Still inside this attempt's grace window — give the respawn time to land.
  if (now - pending.firedAt < deadlineMs) {
    return { kind: 'waiting' };
  }

  // Window lapsed without a verified respawn.
  const attempt = pending.attempts + 1;
  return { kind: 'unverified', attempt, final: attempt >= maxAttempts };
}

/**
 * ENG-6203 — separate, opt-in REMEDIATION gate layered on top of the
 * (still purely observational) {@link decidePostRestartVerification}.
 *
 * `decidePostRestartVerification` only *observes*; ENG-6174 deliberately would
 * not force a re-respawn because forcing one could fight a gate (circuit
 * breaker / maintenance window / kill-switch) that is legitimately holding a
 * session down. This helper draws the principled line: it returns true ONLY at
 * the TERMINAL failure (`unverified` + `final` — the grace windows lapsed and
 * the respawn never came back healthy), and ONLY when remediation is enabled and
 * the breaker is NOT already tripped.
 *
 * The breaker guard is load-bearing: the caller's `scheduleSessionRestart`
 * stops the session *before* the breaker records, and a tripped breaker blocks
 * the subsequent respawn — so remediating into an already-tripped breaker would
 * stop a session it cannot bring back. Gating here means a genuinely-unloadable
 * session degrades to the existing visible ERROR / circuit-tripped state after a
 * bounded number of attempts, never an infinite respawn loop.
 *
 * The remediation itself goes THROUGH the gated `scheduleSessionRestart`
 * (maintenance-window + breaker honored), so this initiates a normal gated
 * restart from a settled state — it never bypasses a gate, preserving ENG-6174's
 * "don't fight whatever is holding it" stance.
 */
export function shouldRemediateUnverifiedRestart(
  outcome: RestartVerifyOutcome,
  ctx: { enabled: boolean; breakerTripped: boolean; authSkipped: boolean },
): boolean {
  return (
    ctx.enabled &&
    !ctx.breakerTripped &&
    // ENG-8581: never remediate a session whose spawn was SKIPPED because the
    // host cannot authenticate to Claude. Re-respawning cannot bind tools when
    // no session can start at all, so the remediation is incapable of succeeding
    // and simply loops. Observed on dig-host: 28 restarts in 6h, then 21 more in
    // the hour around its re-auth, none of which could ever have worked.
    //
    // The circuit breaker does not catch this, which is the other half of the
    // ticket: a skipped spawn returns `spawnAttempted: false`, so a restart that
    // is never ATTEMPTED never registers as a failing restart. The mechanism
    // designed to stop the loop is structurally blind to it.
    !ctx.authSkipped &&
    outcome.kind === 'unverified' &&
    outcome.final
  );
}

/**
 * ENG-7575 — is any of the agent's integrations a freshly-added one still
 * flapping on first-connect? True iff some integration was created within
 * `windowMs` of `now` AND currently reports connectivity `transient_error`.
 *
 * Fail-open by construction: an integration with no `created_at`, an unparseable
 * timestamp, or a status other than `transient_error` never contributes — so a
 * dormant connectivity probe (status never written) yields false and
 * bind-remediation behaves exactly as pre-ENG-7575. A long-lived integration that
 * genuinely breaks (transient_error but created before the window) is likewise
 * not counted, so it still remediates normally.
 *
 * Pure (window passed in) so it lives beside the other verify helpers and is
 * unit-testable without the manager's heavy module load.
 */
export function computeFirstConnectUnsettled(
  integrations: ReadonlyArray<{
    last_connectivity_status?: string | null;
    created_at?: string | null;
    /**
     * ENG-9718 — the reconnect settling deadline. Present only on rows the
     * control plane has recently reconnected.
     */
    reconnect_quiesced_until?: string | null;
  }>,
  now: number,
  windowMs: number,
): boolean {
  return integrations.some((i) => {
    // ENG-9034: `unverified` joined `transient_error` here when the probe stopped
    // grading a missing `.mcp.json` key as a failure. A freshly-added integration
    // whose key has not been written yet now reads `unverified` within the
    // first-connect window, and that is the exact row this backoff exists to
    // defer on — without it the deferral silently stops engaging and
    // bind-remediation goes back to force-respawning to bind tools that cannot
    // bind yet (the ENG-7560 churn loop).
    //
    // `transient_error` is kept, not replaced: rows written before this shipped
    // still carry it, and a genuinely flapping first-connect can still produce
    // it from a different leg. Both mean "still settling" inside the window.
    if (i.last_connectivity_status !== 'transient_error' && i.last_connectivity_status !== 'unverified') {
      return false;
    }
    // ENG-9718: the reconnect anchor moves WITH the probe's. `mcp-config-lookup`
    // now grades a missing key `unverified` inside a reconnect window as well as
    // inside the first-connect window; if this backoff kept keying on
    // `created_at` alone, a reconnected row would sit in the state the header
    // above warns about — the probe saying "still settling" while the remediator
    // force-respawns to bind tools that cannot bind yet. That is the ENG-7560
    // churn loop, and it is silent from here. One anchor set, both callers.
    if (isWithinReconnectQuiesceWindow(i.reconnect_quiesced_until, now)) return true;
    if (!i.created_at) return false;
    const addedAt = Date.parse(i.created_at);
    if (!Number.isFinite(addedAt)) return false;
    return now - addedAt <= windowMs;
  });
}

/**
 * ENG-7575 — first-connect backoff for the ENG-6203 bind-remediation.
 *
 * The aggravating loop from ENG-7560: adding an integration whose connection is
 * still flapping on first-connect (`last_connectivity_status='transient_error'`)
 * fails bind-verification, so bind-remediation forces a re-respawn to re-bind
 * tools that CANNOT bind until the connection settles — which keeps failing, so
 * it re-respawns again. Aggressively fighting a still-propagating first-connect
 * is exactly the provisioning-reload churn ENG-7560 stopped from tripping the
 * breaker; this stops producing it in the first place.
 *
 * Returns true at the SAME terminal point as {@link shouldRemediateUnverifiedRestart}
 * (unverified + final, remediation enabled, breaker not tripped) BUT only while a
 * freshly-added integration is still unsettled AND we are under the per-restart
 * deferral cap. When true the caller re-arms the verification window (waits one
 * more grace window) instead of forcing the re-respawn — a bounded backoff, never
 * an indefinite hold (`deferralsSoFar >= maxDeferrals` falls through to the normal
 * remediation / ERROR path so a persistently-broken integration still surfaces).
 *
 * Fail-open by construction: `firstConnectUnsettled` is false whenever the
 * connectivity probe is dormant / the row was never probed / the integration is
 * not recently-added, so on hosts without `AGT_CONNECTIVITY_PROBE_ENABLED` this
 * never fires and behaviour is identical to pre-ENG-7575.
 */
export function shouldDeferBindRemediationForFirstConnect(
  outcome: RestartVerifyOutcome,
  ctx: {
    enabled: boolean;
    breakerTripped: boolean;
    firstConnectUnsettled: boolean;
    deferralsSoFar: number;
    maxDeferrals: number;
    /** ENG-8581 — see shouldRemediateUnverifiedRestart. */
    authSkipped: boolean;
  },
): boolean {
  return (
    ctx.enabled &&
    !ctx.breakerTripped &&
    // ENG-8581: an auth-skipped session has no first-connect to wait for either.
    // Deferring would re-arm the verify window forever against a session that
    // cannot start, burning the deferral budget on a condition no amount of
    // waiting fixes. Same gate as remediation, for the same reason.
    !ctx.authSkipped &&
    ctx.firstConnectUnsettled &&
    ctx.deferralsSoFar < ctx.maxDeferrals &&
    outcome.kind === 'unverified' &&
    outcome.final
  );
}
